import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonOperatorsAssignment = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Python Fundamentals</div>
                <h1>Assignment & Comparison Operators</h1>
                <p className="hero-lead">
                    Master the foundational operators that power every Python program - from simple variable
                    assignments to complex logical comparisons that drive decision-making in your code.
                </p>
            </div>

            <div className="content-section chapter-intro">
                <p>
                    Operators are the building blocks of any programming language, and Python's operator system
                    is both elegant and powerful. In this chapter, we'll explore two critical categories: assignment
                    operators, which store and update values in memory, and comparison operators, which evaluate
                    relationships between values to produce boolean results. Understanding these operators isn't
                    just about memorizing syntaxit's about developing an intuition for how Python manipulates
                    data at a fundamental level.
                </p>
                <p>
                    Whether you're building a simple calculator, processing user input, or implementing complex
                    business logic, you'll use these operators constantly. By the end of this chapter, you'll
                    understand not just the "what" and "how," but also the "why" behind Python's operator design
                    choices, including common pitfalls that trip up even experienced developers.
                </p>

                <div className="learning-objectives">
                    <h3>What You'll Learn</h3>
                    <ul>
                        <li>How assignment operators work with Python's reference model</li>
                        <li>All compound assignment operators and their performance implications</li>
                        <li>Comparison operators and the critical difference between <code>==</code> and <code>is</code></li>
                        <li>Common mistakes that cause bugs in production code</li>
                        <li>Best practices for writing clean, readable comparisons</li>
                    </ul>
                </div>
            </div>

            <div className="content-section">
                <h2>Assignment Operators: Storing Values in Memory</h2>
                <p>
                    The assignment operator (<code>=</code>) is your primary tool for storing and updating values
                    in Python. Unlike mathematical equality, assignment is a directive: "Take the value on the right
                    and bind it to the name on the left." This distinction is crucial because Python variables are
                    references to objects, not containers that hold values directly.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Basic Assignment</span>
                    </div>
                    <CodeBlock language="python" code={`# Simple assignment
x = 10          # x now references the integer object 10
name = "Alice"  # name references the string object "Alice"
is_valid = True # is_valid references the boolean True

# Multiple assignment
a, b, c = 1, 2, 3  # Assigns 1 to a, 2 to b, 3 to c
x = y = z = 0      # All three reference the same integer 0`} />
                </div>

                <p>
                    When you write <code>x = 10</code>, Python creates (or reuses) an integer object with value 10
                    in memory, then creates a binding in the current namespace from the name "x" to that object.
                    This reference model has important implications for how variables behave, especially with mutable
                    objects like lists and dictionaries.
                </p>

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Pro Tip:</strong> Assignment is not a copy operation. When you assign one variable
                        to another (<code>b = a</code>), both variables reference the same object in memory. This
                        matters significantly with mutable objects.
                    </div></ProTip>

            </div>

            <div className="content-section">
                <h2>Advanced Assignment: Unpacking Power</h2>
                <p>
                    One of Python's most beloved features is "Unpacking" (also known as Destructuring).
                    It allows you to assign multiple variables from a single iterable in one clean line.
                </p>

                <h3>1. Tuple Unpacking & Swapping</h3>
                <p>
                    Why write three lines to swap variables when you can write one?
                </p>
                <CodeBlock language="python" code={`# The Old Way (C/Java style)
temp = a
a = b
b = temp

# The Python Way
a, b = b, a  # Swaps values instanty!

# Unpacking a tuple
coordinates = (10, 20)
x, y = coordinates  # x=10, y=20`} />

                <h3>2. Extended Iterable Unpacking (*)</h3>
                <p>
                    Introduced in Python 3, the asterisk (<code>*</code>) operator allows you to grab "everything else" into a list.
                    This is incredibly useful when parsing data streams or CSV rows.
                </p>
                <CodeBlock language="python" code={`# Grab the first and last, ignore the middle
scores = [10, 50, 60, 90, 100]
first, *middle, last = scores

print(first)   # 10
print(middle)  # [50, 60, 90] (Always a list!)
print(last)    # 100

# Head/Tail separation (LISP style)
head, *tail = [1, 2, 3, 4]
print(head) # 1
print(tail) # [2, 3, 4]`} />

                <h3>3. Deep Unpacking & Ignored Values</h3>
                <p>
                    You can unpack nested structures and use <code>_</code> (underscore) for values you don't care about.
                </p>
                <CodeBlock language="python" code={`user_record = ("Alice", 25, ("NY", "USA"))

# Unpack name and country only
name, _, (_, country) = user_record

print(name)     # "Alice"
print(country)  # "USA"
# _ is assigned 25, but we signal "ignore this"`} />

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Performance Note:</strong> Unpacking is not just syntactic sugar; it compiles to specific bytecode instructions
                        (<code>UNPACK_SEQUENCE</code>) which are often faster than manual indexing (<code>x = data[0]</code>).
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>Compound Assignment Operators</h2>
                <p>
                    Compound assignment operators combine an operation with assignment, providing a concise way to
                    update variables. These operators are not just syntactic sugarthey can be more efficient and
                    express intent more clearly than their expanded forms.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>All Compound Assignment Operators</span>
                    </div>
                    <CodeBlock language="python" code={`# Arithmetic compound assignments
count = 10
count += 5   # count = count + 5   15
count -= 3   # count = count - 3   12
count *= 2   # count = count * 2   24
count /= 4   # count = count / 4   6.0 (float division)
count //= 2  # count = count // 2  3.0 (floor division)
count %= 2   # count = count % 2   1.0 (modulo)
count **= 3  # count = count ** 3  1.0 (exponentiation)

# String concatenation
message = "Hello"
message += " World"  # "Hello World"

# List extension
numbers = [1, 2, 3]
numbers += [4, 5]    # [1, 2, 3, 4, 5]`} />
                </div>

                <p>
                    Each compound operator modifies the variable in place when possible. For immutable types like
                    integers and strings, a new object is created and the variable is rebound. For mutable types
                    like lists, the object itself is modified, which can have surprising effects when multiple
                    variables reference the same list.
                </p>

                <div className="comparison-table">
                    <h3>Compound Assignment Operators Reference</h3>
                    <table>
                        <thead>
                            <tr>
                                <th>Operator</th>
                                <th>Example</th>
                                <th>Equivalent To</th>
                                <th>Description</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>+=</code></td>
                                <td><code>x += 3</code></td>
                                <td><code>x = x + 3</code></td>
                                <td>Add and assign</td>
                            </tr>
                            <tr>
                                <td><code>-=</code></td>
                                <td><code>x -= 3</code></td>
                                <td><code>x = x - 3</code></td>
                                <td>Subtract and assign</td>
                            </tr>
                            <tr>
                                <td><code>*=</code></td>
                                <td><code>x *= 3</code></td>
                                <td><code>x = x * 3</code></td>
                                <td>Multiply and assign</td>
                            </tr>
                            <tr>
                                <td><code>/=</code></td>
                                <td><code>x /= 3</code></td>
                                <td><code>x = x / 3</code></td>
                                <td>Divide and assign (float)</td>
                            </tr>
                            <tr>
                                <td><code>//=</code></td>
                                <td><code>x //= 3</code></td>
                                <td><code>x = x // 3</code></td>
                                <td>Floor divide and assign</td>
                            </tr>
                            <tr>
                                <td><code>%=</code></td>
                                <td><code>x %= 3</code></td>
                                <td><code>x = x % 3</code></td>
                                <td>Modulo and assign</td>
                            </tr>
                            <tr>
                                <td><code>**=</code></td>
                                <td><code>x **= 3</code></td>
                                <td><code>x = x ** 3</code></td>
                                <td>Exponentiate and assign</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="content-section">
                <h2>The Walrus Operator (:=)</h2>
                <p>
                    Introduced in Python 3.8, the Assignment Expression operator (<code>:=</code>) allows you to assign a value to a
                    variable <em>inside</em> an expression. It is a powerful tool for condensing loops and comprehensions.
                </p>
                <p>
                    Because this operator fundamentally changes how Python expressions work, we have dedicated an entire lesson to it.
                    Learn how to use it to clean up <code>while</code> loops and optimize list comprehensions in the dedicated module.
                </p>
                <div className="cta-buttons">
                    <a href="/insights/courses/python/python-walrus-operator" className="btn-secondary">
                        Go to Walrus Operator Lesson 
                    </a>
                </div>
            </div>

            <h3>Bitwise Compound Assignment</h3>
            <p>
                While arithmetic assignments are common, bitwise assignments (`|=`, `&=`) are secret weapons for managing state flags efficiently.
            </p>
            <CodeBlock language="python" code={`# Permissions System
READ = 1   # 001
WRITE = 2  # 010
EXEC = 4   # 100

user_perms = 0

# Grant permissions (Bitwise OR)
user_perms |= READ   # 001
user_perms |= WRITE  # 011

# Revoke permissions (Bitwise AND with Inverse)
user_perms &= ~WRITE # 001

# Check permission
has_read = (user_perms & READ) == READ`} />


            <div className="content-section">
                <h2>Comparison Operators: Evaluating Relationships</h2>
                <p>
                    Comparison operators evaluate the relationship between two values and return a boolean result
                    (<code>True</code> or <code>False</code>). These operators are the foundation of conditional
                    logic, enabling your programs to make decisions based on data. Python supports six comparison
                    operators, each serving a specific purpose in logical expressions.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Comparison Operators in Action</span>
                    </div>
                    <CodeBlock language="python" code={`x = 10
y = 5

# Equality and inequality
print(x == y)   # False - Equal to?
print(x != y)   # True  - Not equal to?

# Relational comparisons
print(x &gt; y)    # True  - Greater than?
print(x < y)    # False - Less than?
print(x >= 10)  # True  - Greater than or equal to?
print(x <= 5)   # False - Less than or equal to?

# Chained comparisons (Pythonic!)
age = 25
print(18 <= age < 65)  # True - age is between 18 and 65`} />
                </div>

                <p>
                    Python's comparison operators work intuitively with numbers, but they also support comparisons
                    between strings (lexicographic order), lists (element-by-element), and even custom objects if
                    you implement the appropriate magic methods. The ability to chain comparisons
                    (<code>a &lt; b &lt; c</code>) is a Pythonic feature that makes range checks incredibly readable.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Comparing Different Data Types</span>
                    </div>
                    <CodeBlock language="python" code={`# String comparison (lexicographic order)
print("apple" < "banana")   # True
print("Python" == "python") # False (case-sensitive)

# List comparison (element by element)
print([1, 2, 3] < [1, 2, 4])  # True
print([1, 2] < [1, 2, 0])     # False

# Tuple comparison
print((1, 2) < (1, 3))  # True
print((2, 1) < (1, 3))  # False`} />
                </div>
            </div>

            <div className="content-section">
                <h2>The Critical Difference: == vs is</h2>
                <p>
                    One of the most important distinctions in Python is between the <code>==</code> operator and
                    the <code>is</code> operator. While <code>==</code> checks for value equality (are these two
                    things equal?), <code>is</code> checks for identity (are these the exact same object in memory?).
                    Confusing these two is a common source of subtle bugs.
                </p>

                <div className="code-example">
                    <div className="code-header">
                        <span>Equality vs Identity</span>
                    </div>
                    <CodeBlock language="python" code={`# Equality (==) - compares values
list1 = [1, 2, 3]
list2 = [1, 2, 3]
print(list1 == list2)  # True - same values
print(list1 is list2)  # False - different objects

# Identity (is) - compares object IDs
list3 = list1
print(list1 is list3)  # True - exact same object

# The None check (the correct use of 'is')
value = None
if value is None:  # Correct
    print("Value is None")

if value == None:  #  Works but not recommended
    print("This works but use 'is' instead")`} />
                </div>

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Important:</strong> Always use <code>is</code> when comparing to <code>None</code>,
                        <code>True</code>, or <code>False</code>. These are singleton objects in Python, and identity
                        checks are both faster and more semantically correct.
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>Cloning Data: Shallow vs Deep Copy</h2>
                <p>
                    Since assignment (<code>=</code>) only creates references, how do you actually <em>copy</em> an object?
                    If you want to modify a list without affecting the original, you need to clone it.
                    Python offers two ways to do this, and mixing them up is a common source of bugs.
                </p>

                <h3>1. Shallow Copy</h3>
                <p>
                    Creates a new container, but populates it with <strong>references</strong> to the same child objects.
                    This is the default behavior of slicing <code>[:]</code> and the <code>list()</code> constructor.
                </p>
                <CodeBlock language="python" code={`original = [[1, 2], [3, 4]]
shallow = original[:]  # or list(original)

# Modifying the container is safe...
shallow.append([5, 6])
print(original) # [[1, 2], [3, 4]] (Unaffected)

# BUT modifying a child affects BOTH!
shallow[0][0] = 999
print(original) # [[999, 2], [3, 4]] (Affected!)
# Why? Because shallow[0] and original[0] point to the SAME list object.`} />

                <h3>2. Deep Copy</h3>
                <p>
                    Recursively copies everything. It creates a new container AND new child objects (and new children of children...).
                    This is slower but truly independent.
                </p>
                <CodeBlock language="python" code={`import copy

original = [[1, 2], [3, 4]]
deep = copy.deepcopy(original)

deep[0][0] = 999
print(original) # [[1, 2], [3, 4]] (Clean!)`} />
            </div>

            <div className="content-section">
                <h2>The Scope of Assignment: global and nonlocal</h2>
                <p>
                    By default, when you assign to a variable inside a function (<code>x = 10</code>), Python creates a <strong>local</strong> variable,
                    even if a global variable with the same name exists. This protects your global state from accidental modification.
                    But what if you <em>want</em> to modify the outer state?
                </p>

                <h3>1. The `global` Keyword</h3>
                <p>
                    Use `global` to tell Python: "Don't create a new local variable; use the one defined at the top level."
                </p>
                <CodeBlock language="python" code={`counter = 0

def increment():
    # counter += 1  #  UnboundLocalError! Python sees assignment and assumes local.
    
    global counter
    counter += 1    # Modifies the global variable
    print(counter)`} />

                <h3>2. The `nonlocal` Keyword</h3>
                <p>
                    Used in nested functions (closures). It tells Python to look in the nearest enclosing scope that isn't global.
                </p>
                <CodeBlock language="python" code={`def outer():
    count = 0
    
    def inner():
        nonlocal count
        count += 1
        return count
        
    return inner

# This creates a stateful closure!
fn = outer()
print(fn()) # 1
print(fn()) # 2`} />

                <ProTip><div className="info-icon"></div>
                    <div className="info-text">
                        <strong>Architecture Warning:</strong> heavily relying on <code>global</code> is often a sign of poor design.
                        It makes code hard to test and debug because functions have "hidden inputs" (the global state).
                        Prefer passing arguments and returning values.
                    </div></ProTip>
            </div>

            <div className="content-section">
                <h2>Real-World Applications</h2>

                <h3>1. Input Validation Logic</h3>
                <p>
                    One of the most common uses for comparison operators is validating user input.
                    Whether you are building a CLI tool or a Web API, you must ensure data falls within safe boundaries.
                </p>
                <CodeBlock language="python" code={`def validate_age(age):
    """Validate age is within acceptable range"""
    # Chained comparison simplifies logic
    if not (0 <= age <= 150):
        raise ValueError("Age must be between 0 and 150")
    
    # Categorization logic
    if age < 18:
        return "minor"
    elif age >= 65:
        return "senior"
    else:
        return "adult"

def validate_password(password):
    """Check constraints using short-circuit logic"""
    # Fail fast if length is wrong
    if len(password) < 8:
        return False
    
    # Check composition using any()
    # These return Booleans directly - no need for 'if'
    has_digit = any(c.isdigit() for c in password)
    has_upper = any(c.isupper() for c in password)
    
    return has_digit and has_upper`} />

                <h3>2. The Accumulator Pattern</h3>
                <p>
                    Compound assignment operators (<code>+=</code>) are the backbone of "Accumulator" classes,
                    used for statistics, scoring, or logging. They are cleaner than <code>x = x + 1</code>.
                </p>
                <CodeBlock language="python" code={`class WebsiteAnalytics:
    def __init__(self):
        self.page_views = 0
        self.total_time = 0.0
    
    def record_visit(self, duration):
        # Update multiple metrics in place
        self.page_views += 1
        self.total_time += duration
    
    def average_time(self):
        # Compare vs 0 to avoid ZeroDivisionError
        if self.page_views == 0:
            return 0
        return self.total_time / self.page_views`} />

                <h3>3. Filtering & Comprehensions</h3>
                <p>
                    Comparison operators define the logic for filtering datasets.
                    When used inside List Comprehensions, they create a declarative way to query data.
                </p>
                <CodeBlock language="python" code={`products = [
    {'{'}"name": "Laptop", "price": 999, "rating": 4.5{'}'},
    {'{'}"name": "Mouse", "price": 25, "rating": 4.2{'}'},
    {'{'}"name": "Keyboard", "price": 75, "rating": 4.7{'}'}
]

# "Select * from products where price between 20 and 100"
affordable = [
    p for p in products 
    if 20 <= p["price"] <= 100
]

# "Select * from products order by price"
# Lambda uses the comparison value
sorted_products = sorted(products, key=lambda p: p["price"])`} />
            </div>

            <div className="content-section common-pitfalls">
                <h2>Common Pitfalls & How to Avoid Them</h2>

                <div className="pitfall-card">
                    <h3> Mistake 1: Using = Instead of == in Conditions</h3>
                    <p><strong>Why it's wrong:</strong> Assignment (<code>=</code>) instead of comparison
                        (<code>==</code>) in an <code>if</code> statement will cause a <code>SyntaxError</code>
                        in Python (unlike C/JavaScript where it silently assigns).</p>
                    <CodeBlock language="python" code={`#  WRONG - SyntaxError
if x = 10:
    print("This won't work")

# CORRECT
if x == 10:
    print("This works!")`} />
                    <p><strong>How to avoid:</strong> Python actually protects you from this error! The syntax
                        error is intentional. In other languages, this is a dangerous bug, but Python's design prevents it.</p>
                </div>

                <div className="pitfall-card">
                    <h3> Mistake 2: Comparing Floats with ==</h3>
                    <p><strong>Why it's wrong:</strong> Floating-point arithmetic is imprecise due to binary
                        representation. Direct equality checks can fail unexpectably.</p>
                    <CodeBlock language="python" code={`#  WRONG - Unreliable
result = 0.1 + 0.2
if result == 0.3:  # Might be False!
    print("Equal")

print(0.1 + 0.2)  # 0.30000000000000004

# CORRECT - Use tolerance
import math
import { ProTip } from '../../components/LessonUI';
import { ProTip } from '../../components/LessonUI';

def float_equal(a, b, tolerance=1e-9):
    return abs(a - b) < tolerance

if float_equal(0.1 + 0.2, 0.3):
    print("Equal within tolerance")

# Or use the math module
if math.isclose(0.1 + 0.2, 0.3):
    print("Close enough!")`} />
                </div>

                <div className="pitfall-card">
                    <h3> Mistake 3: Using is for Value Comparison</h3>
                    <p><strong>Why it's wrong:</strong> <code>is</code> checks object identity, not value equality.
                        Due to Python's integer caching, it might work for small numbers but fail for larger ones.</p>
                    <CodeBlock language="python" code={`#  WRONG - Works sometimes, fails others
a = 1000
b = 1000
if a is b:  # False (different objects)
    print("Same")

# This confusingly works due to integer caching
x = 5
y = 5
if x is y:  # True (cached integers -5 to 256)
    print("Same")

# CORRECT - Use == for value comparison
if a == b:
    print("Equal values")

# Only use 'is' for None, True, False
if value is None:
    print("Value is None")`} />
                </div>

                <div className="pitfall-card">
                    <h3> Mistake 4: Unexpected Behavior with += on Lists</h3>
                    <p><strong>Why it's wrong:</strong> <code>+=</code> modifies lists in-place, affecting all
                        references to that list object.</p>
                    <CodeBlock language="python" code={`#  UNEXPECTED BEHAVIOR
original = [1, 2, 3]
alias = original
alias += [4, 5]

print(original)  # [1, 2, 3, 4, 5] - Modified!
print(alias)     # [1, 2, 3, 4, 5]

# CORRECT - Create a new list if you don't want to modify
original = [1, 2, 3]
new_list = original + [4, 5]  # Creates new list

print(original)  # [1, 2, 3] - Unchanged
print(new_list)  # [1, 2, 3, 4, 5]`} />
                </div>


                <div className="pitfall-card">
                    <h3> Mistake 5: The Mutable Default Argument</h3>
                    <p><strong>Why it's wrong:</strong> Default arguments are evaluated <strong>only once</strong> when the function is defined, not every time it is called.
                        If you use a mutable object (like a list) as a default, it persists across calls.</p>
                    <CodeBlock language="python" code={`#  WRONG
def add_item(item, data=[]):
    data.append(item)
    return data

print(add_item(1))  # [1]
print(add_item(2))  # [1, 2]  - The list grew!

# CORRECT - Use None as a sentinel
def add_item_safe(item, data=None):
    if data is None:
        data = []  # Create a NEW list every time
    data.append(item)
    return data`} />
                </div>
            </div>

            <div className="content-section best-practices">
                <h2>Best Practices</h2>

                <div className="practice-grid">
                    <div className="practice-card">
                        <h3>Do:</h3>
                        <ul>
                            <li>Use compound assignment operators (<code>+=</code>, <code>-=</code>) for clarity and efficiency</li>
                            <li>Always use <code>is</code> when comparing to <code>None</code></li>
                            <li>Chain comparison operators for range checks: <code>0 &lt;= x &lt; 100</code></li>
                            <li>Use <code>math.isclose()</code> for floating-point comparisons</li>
                            <li>Be explicit with parentheses in complex comparisons</li>
                            <li>Use <code>!=</code> instead of <code>not ==</code> for clarity</li>
                        </ul>
                    </div>

                    <div className="practice-card">
                        <h3> Don't:</h3>
                        <ul>
                            <li>Don't use <code>== True</code> or <code>== False</code> - use the value directly</li>
                            <li>Don't use <code>is</code> for value comparisons (except <code>None</code>)</li>
                            <li>Don't compare floats with <code>==</code> without tolerance</li>
                            <li>Don't chain too many assignments (<code>a = b = c = []</code> with mutable objects)</li>
                            <li>Don't forget operator precedence in complex expressions</li>
                            <li>Don't use assignment in conditions (Python prevents this anyway)</li>
                        </ul>
                    </div>
                </div>
            </div>

            <div className="content-section key-takeaways">
                <h2> Key Takeaways</h2>
                <div className="takeaway-grid">
                    <div className="takeaway-item">
                        <h3>1. Assignment Binds Names to Objects</h3>
                        <p>
                            The <code>=</code> operator creates a reference from a variable name to an object in memory,
                            not a copy. This matters especially with mutable objects.
                        </p>
                    </div>

                    <div className="takeaway-item">
                        <h3>2. Compound Operators Are Efficient</h3>
                        <p>
                            Operators like <code>+=</code> and <code>*=</code> modify variables in-place when possible,
                            making code more concise and often more performant.
                        </p>
                    </div>

                    <div className="takeaway-item">
                        <h3>3. == Checks Value, is Checks Identity</h3>
                        <p>
                            Use <code>==</code> to compare values (equality) and <code>is</code> to check if two
                            variables reference the same object (identity). Always use <code>is None</code>.
                        </p>
                    </div>

                    <div className="takeaway-item">
                        <h3>4. Comparison Operators Return Booleans</h3>
                        <p>
                            All comparison operators (<code>&lt;</code>, <code>&gt;</code>, <code>==</code>, etc.)
                            return <code>True</code> or <code>False</code>, forming the foundation of conditional logic.
                        </p>
                    </div>

                    <div className="takeaway-item">
                        <h3>5. Chain Comparisons for Readability</h3>
                        <p>
                            Python allows chaining: <code>a &lt; b &lt; c</code> is more readable and efficient than
                            <code>a &lt; b and b &lt; c</code>.
                        </p>
                    </div>

                    <div className="takeaway-item">
                        <h3>6. Beware of Floating-Point Comparison</h3>
                        <p>
                            Never use <code>==</code> for floats directly. Use <code>math.isclose()</code> with
                            appropriate tolerance instead.
                        </p>
                    </div>
                </div>
            </div>

        </div >
    );
};

export default PythonOperatorsAssignment;

